import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config';

// إنشاء نسخة مُعدلة من axios مع تكوين أساسي
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000, // 20 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  }
});

// إضافة معترض للطلبات لإضافة رمز المصادقة
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // الحصول على رمز المصادقة من التخزين المحلي
  const token = localStorage.getItem('token');
  
  // إضافة رمز المصادقة كرأس إذا كان موجودًا
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
}, (error: AxiosError) => {
  return Promise.reject(error);
});

// إضافة معترض للاستجابات للتعامل مع أخطاء المصادقة
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // يتم استخراج URL الطلب لمعرفة نوع الطلب
    const requestUrl = error.config?.url || '';
    
    // التحقق ما إذا كان الطلب متعلق بتحويلات المستودعات
    const isWarehouseTransferRequest = requestUrl.includes('warehouse-transfers');
    
    // التعامل مع خطأ 401 غير مصرح
    if (error.response?.status === 401) {
      console.error('خطأ في المصادقة:', error.response.data);
      
      // لا نقوم بإعادة التوجيه إلى صفحة تسجيل الدخول لطلبات تحويلات المستودعات
      if (isWarehouseTransferRequest) {
        console.log('تخطي إعادة التوجيه إلى صفحة تسجيل الدخول لطلب تحويلات المستودعات');
        // إعادة الخطأ دون إعادة توجيه
      } else if (!window.location.pathname.includes('/login')) {
        // بالنسبة لجميع الطلبات الأخرى، نعرض رسالة للمستخدم
        alert('انتهت جلسة العمل أو لم يتم تسجيل الدخول. سيتم توجيهك إلى صفحة تسجيل الدخول.');
        
        // إعادة توجيه إلى صفحة تسجيل الدخول
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// وظائف واجهة API
export const apiService = {
  // طلب GET
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.get<T>(url, config);
      return response.data;
    } catch (error) {
      console.error(`خطأ في طلب GET إلى ${url}:`, error);
      throw error;
    }
  },
  
  // طلب POST
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.post<T>(url, data, config);
      return response.data;
    } catch (error) {
      console.error(`خطأ في طلب POST إلى ${url}:`, error);
      throw error;
    }
  },
  
  // طلب PUT
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.put<T>(url, data, config);
      return response.data;
    } catch (error) {
      console.error(`خطأ في طلب PUT إلى ${url}:`, error);
      throw error;
    }
  },
  
  // طلب PATCH
  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.patch<T>(url, data, config);
      return response.data;
    } catch (error) {
      console.error(`خطأ في طلب PATCH إلى ${url}:`, error);
      throw error;
    }
  },
  
  // طلب DELETE
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.delete<T>(url, config);
      return response.data;
    } catch (error) {
      console.error(`خطأ في طلب DELETE إلى ${url}:`, error);
      throw error;
    }
  },
  
  // طلب GET لتحميل ملف (Blob)
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    try {
      const response = await api.get(url, {
        ...config,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error(`خطأ في طلب GET Blob إلى ${url}:`, error);
      throw error;
    }
  },
  
  // طلب POST لرفع ملف (FormData)
  async postFormData<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.post<T>(url, formData, {
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...config?.headers
        }
      });
      return response.data;
    } catch (error) {
      console.error(`خطأ في طلب POST FormData إلى ${url}:`, error);
      throw error;
    }
  },
  
  // دالة للتحقق من حالة المصادقة
  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  },
  
  // دالة للتحقق من صحة رمز المصادقة
  async validateToken(): Promise<boolean> {
    if (!this.isAuthenticated()) {
      return false;
    }
    
    try {
      await this.get('/auth/verify-token');
      return true;
    } catch (error) {
      console.error('فشل التحقق من رمز المصادقة:', error);
      // إزالة الرمز غير الصالح
      localStorage.removeItem('token');
      return false;
    }
  }
};

export default apiService;